/**
 * Reusable CategoryBadge component
 * Displays category with emoji icon
 * Uses the same icons as the timeline events for consistency
 */

import { Badge } from '@/components/ui/badge';

interface CategoryBadgeProps {
  category: string;
  variant?: 'default' | 'secondary' | 'outline' | 'destructive';
  className?: string;
}

// Icon mapping for categories - matches timeline event icons
const CATEGORY_ICONS: Record<string, string> = {
  'biblical-composition': '📜',
  'biblical-narrative': '📖',
  'israel-ruler': '👑',
  'israel-prophet': '📣',
  'empire': '🏛️',
  'people': '🏘️',
  'political': '⚖️',
  'military': '⚔️',
  'religious': '🙏',
  'cultural': '🎭',
  'economic': '💰',
  'technological': '⚙️',
  'natural': '🌊',
  'diaspora': '🚶',
  // Archaeological categories
  'pottery': '🏺',
  'architecture': '🏛️',
  'inscriptions': '📜',
  'coins': '🪙',
  'weapons': '⚔️',
  'tools': '🔨',
  'jewelry': '💎',
  'burial': '⚰️',
  'temple': '🕍',
  'fortification': '🏰',
  // Cultural categories
  'social-custom': '👥',
  'religious-practice': '🙏',
  'economic-practice': '💰',
  'legal': '⚖️',
  'family': '👨‍👩‍👧‍👦',
  'agriculture': '🌾',
  'trade': '🤝',
  'warfare': '⚔️',
  'education': '📚',
  'art': '🎨'
};

// Normalize category string for icon lookup
function normalizeCategory(category: string): string {
  return category.toLowerCase().replace(/\s+/g, '-');
}

export function CategoryBadge({ category, variant = 'outline', className = '' }: CategoryBadgeProps) {
  const normalizedCategory = normalizeCategory(category);
  const icon = CATEGORY_ICONS[normalizedCategory];

  return (
    <Badge
      variant={variant}
      className={`text-[10px] uppercase tracking-wider font-medium flex items-center gap-1 ${className}`}
    >
      {icon && <span className="text-xs">{icon}</span>}
      {category}
    </Badge>
  );
}

